import type { SupabaseClient } from '@supabase/supabase-js';
import { captureEdgeFunctionFailure, captureSupabaseFailure } from '../sentry';

function reportTableError(
  table: string,
  operation: string,
  error: unknown,
  context?: Record<string, string>,
): void {
  if (!error) return;
  captureSupabaseFailure(table, operation, error, context);
}

type PostgrestResult = { data: unknown; error: unknown };

function wrapPostgrestBuilder(builder: unknown, table: string, operation = 'query'): unknown {
  if (!builder || typeof builder !== 'object') return builder;

  return new Proxy(builder as object, {
    get(target, prop, receiver) {
      if (prop === 'then') {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value !== 'function') return value;
        return (onFulfilled?: (v: PostgrestResult) => unknown, onRejected?: (e: unknown) => unknown) =>
          Promise.resolve(target as PostgrestResult).then(
            (res) => {
              if (res?.error) reportTableError(table, operation, res.error);
              return onFulfilled ? onFulfilled(res) : res;
            },
            onRejected,
          );
      }

      const value = Reflect.get(target, prop, receiver);
      if (typeof value === 'function') {
        return (...args: unknown[]) => {
          const next = value.apply(target, args);
          if (next && typeof next === 'object' && typeof (next as { then?: unknown }).then === 'function') {
            return wrapPostgrestBuilder(next, table, String(prop));
          }
          return next;
        };
      }
      return value;
    },
  });
}

function edgeBodyError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const err = (data as { error?: unknown }).error;
  if (typeof err === 'string' && err.trim()) return err.trim();
  return null;
}

/** Wrap Supabase client — auto-reports failed PostgREST, RPC, and edge invoke responses. */
export function observeSupabaseClient(client: SupabaseClient): SupabaseClient {
  return new Proxy(client, {
    get(target, prop, receiver) {
      if (prop === 'from') {
        return (table: string) => wrapPostgrestBuilder(target.from(table), table, 'query');
      }

      if (prop === 'rpc') {
        return (fn: string, params?: unknown, options?: unknown) => {
          const builder = target.rpc(fn, params as never, options as never);
          return wrapPostgrestBuilder(builder, `rpc:${fn}`, 'rpc');
        };
      }

      if (prop === 'functions') {
        const functions = target.functions;
        return new Proxy(functions, {
          get(fTarget, fProp, fReceiver) {
            if (fProp === 'invoke') {
              return async (functionName: string, options?: unknown) => {
                const res = await functions.invoke(functionName, options as never);
                if (res.error) {
                  captureEdgeFunctionFailure(functionName, 'invoke', res.error);
                }
                const bodyErr = edgeBodyError(res.data);
                if (bodyErr) {
                  captureEdgeFunctionFailure(functionName, 'invoke_body', new Error(bodyErr));
                }
                return res;
              };
            }
            return Reflect.get(fTarget, fProp, fReceiver);
          },
        });
      }

      return Reflect.get(target, prop, receiver);
    },
  }) as SupabaseClient;
}
